.Button {
  align-items: center;
  border: none;
  border-color: transparent;
  border-radius: var(--border-radius-md);
  border-style: solid;
  border-width: var(--space-xxs);
  color: var(--content-foundation-content-tertiary);
  cursor: pointer;
  display: inline-flex;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  gap: var(--space-md);
  justify-content: center;
  line-height: var(--line-height-sm);
  margin: 0;
  padding: var(--space-md) var(--space-xl);
  transition: all 0.2s;
  white-space: nowrap;
  width: auto;

  &:disabled {
    cursor: not-allowed;
  }

  .ButtonChild {
    background-color: transparent;
    width: auto;
  }
}

.Button.outlined {
  background-color: transparent;
}

.Button:has(.IconBefore):not(.IconAfter) {
  padding-left: var(--space-lg);
  padding-right: var(--space-xl);
}

.Button:has(.IconAfter):not(.IconBefore) {
  padding-left: var(--space-xl);
  padding-right: var(--space-lg);
}

.Button:has(.IconBefore):has(.IconAfter) {
  padding-left: var(--space-xl);
  padding-right: var(--space-xl);
}

.IconBefore,
.IconAfter {
  color: inherit;
  height: var(--icon-size-sm);
  padding: 0;
}

.SpinnerContainer .SpinnerItem {
  opacity: 100%;
}

.SpinnerContainer {
  height: var(--icon-size-sm);
  padding: var(--space-md) 0;

  .SpinnerItem:has(.tiny) {
    padding: var(--space-xs) 0;
  }
}

:global(.rtl) {
  & .Button + .Button {
    margin-left: 0;
    margin-right: var(--space-md);
  }
}

.block {
  width: 100%;
}

.small {
  font-size: var(--font-size-xs);
  gap: var(--space-xs);
  line-height: var(--line-height-sm);
  padding: var(--space-md) var(--space-lg);

  .IconBefore,
  .IconAfter {
    height: var(--icon-size-sm);
  }
}

.small:has(.IconBefore):not(.IconAfter) {
  padding-left: var(--space-md);
  padding-right: var(--space-lg);
}

.small:has(.IconAfter):not(.IconBefore) {
  padding-left: var(--space-lg);
  padding-right: var(--space-md);
}

.small:has(.IconBefore):has(.IconAfter) {
  padding-left: var(--space-lg);
  padding-right: var(--space-lg);
}

.tiny {
  font-size: var(--font-size-xs);
  gap: var(--space-xs);
  padding: var(--space-xxs) var(--space-md);

  .IconBefore,
  .IconAfter {
    color: inherit;
    height: var(--icon-size-xs);
    padding: 0;
  }
}

.tiny:has(.IconBefore):not(.IconAfter) {
  padding-left: var(--space-xs);
  padding-right: var(--space-md);
}

.tiny:has(.IconAfter):not(.IconBefore) {
  padding-left: var(--space-md);
  padding-right: var(--space-xs);
}

.tiny:has(.IconBefore):has(.IconAfter) {
  padding-left: var(--space-md);
  padding-right: var(--space-md);
}

.blue {
  background-color: var(--background-surface-foundation-background-accent-a);
  border-color: var(--background-surface-foundation-background-accent-a);
  color: var(--white);

  &:disabled {
    background-color: var(--background-surface-core-background-accent-a-dull);
    border-color: var(--border-core-border-accent-a-light);
  }

  &:focus:not(:disabled),
  &:disabled.loading {
    background-color: var(--background-surface-foundation-background-accent-a);
  }

  &:focus:not(:disabled) {
    border-color: var(--border-core-border-accent-b-light);
  }

  &:hover:not(:disabled) {
    background: var(--background-surface-core-background-accent-a-dark);
  }

  &.outlined {
    background-color: var(--background-surface-foundation-background-primary);
    border: 1px solid var(--border-foundation-border-accent-a);
    color: var(--background-surface-foundation-background-accent-a);

    &:hover {
      background-color: var(
        --background-surface-core-background-accent-a-light
      );
    }

    &:disabled {
      background-color: var(--background-surface-foundation-background-primary);
      border-color: var(--color-blue-500-alpha-50);
      color: var(--content-core-content-accent-a-light);
    }

    &:focus:not(:disabled) {
      background-color: var(
        --background-surface-core-background-accent-a-state-focus
      );
    }

    &.loading {
      background-color: transparent;
      border: 1px solid var(--border-foundation-border-accent-a);
    }

    & .SpinnerItem {
      background-color: var(--color-blue-500);
    }
  }

  &.light {
    background-color: var(--background-surface-core-background-accent-a-light);
    border-color: var(--background-surface-core-background-accent-a-light);
    color: var(--content-foundation-accent-a);

    &:disabled {
      color: var(--content-foundation-content-accent-a-light);
    }

    &:disabled,
    &:focus:not(:disabled) {
      background-color: var(
        --background-surface-core-background-accent-a-light
      );
    }

    &:hover:not(:disabled) {
      background: var(
        --background-surface-core-background-accent-a-state-focus
      );
    }

    &:focus:not(:disabled) {
      border-color: var(--border-core-border-accent-b-light);
    }

    & .SpinnerItem {
      background-color: var(--color-blue-500);
    }
  }
}

.teal {
  background-color: var(--background-surface-foundation-background-accent-b);
  border-color: var(--background-surface-foundation-background-accent-b);
  color: var(--white);

  &:disabled {
    background-color: var(
      --background-surface-foundation-background-accent-b-dull
    );
    border-color: var(--border-foundation-border-accent-b-light);
  }

  &:focus:not(:disabled),
  &:disabled.loading {
    background-color: var(--background-surface-foundation-background-accent-b);
  }

  &:hover:not(:disabled) {
    background: var(--background-surface-core-background-accent-b-dark);
  }

  &:focus:not(:disabled) {
    border-color: var(--color-teal-500-alpha-50);
  }

  &.outlined {
    color: var(--content-foundation-content-accent-b);

    &:disabled {
      background-color: var(--background-surface-foundation-background-primary);
      border-color: var(--border-foundation-border-accent-b-light);
      color: var(--content-core-content-accent-b-light);
    }

    &:hover:not(:disabled) {
      background-color: var(
        --background-surface-core-background-accent-b-light
      );
    }

    &:focus:not(:disabled) {
      background-color: var(
        --background-surface-core-background-accent-b-state-focus
      );
    }

    &.loading {
      border-color: var(--border-foundation-border-accent-b);
    }

    & .SpinnerItem {
      background-color: var(--color-teal-500);
    }
  }

  &.light {
    background-color: var(--background-surface-core-background-accent-b-light);
    border-color: var(--background-surface-core-background-accent-b-light);
    color: var(--content-foundation-accent-b);

    &:disabled {
      color: var(--content-core-content-accent-b-light);
    }

    &:disabled,
    &:focus:not(:disabled) {
      background-color: var(
        --background-surface-core-background-accent-b-light
      );
    }

    &:hover:not(:disabled) {
      background: var(
        --background-surface-core-background-accent-b-state-focus
      );
    }

    &:focus:not(:disabled) {
      border-color: var(--border-core-border-accent-b-light);
    }

    & .SpinnerItem {
      background-color: var(--color-teal-500);
    }
  }
}

.orange {
  background-color: var(--background-surface-foundation-background-warning);
  border-color: var(--background-surface-foundation-background-warning);
  color: var(--white);

  &:disabled {
    background-color: var(
      --background-surface-foundation-background-warning-dull
    );
  }

  &:focus:not(:disabled),
  &:disabled.loading {
    background-color: var(--background-surface-foundation-background-warning);
  }

  &:focus:not(:disabled) {
    border-color: var(--border-core-border-warning-light);
  }

  &:hover:not(:disabled) {
    background-color: var(--background-surface-core-background-warning-dark);
  }

  &.outlined {
    border: 1px solid var(--border-foundation-border-warning);
    color: var(--content-foundation-content-warning);

    &:disabled {
      background-color: var(--background-surface-foundation-background-primary);
      border-color: var(--color-orange-500-alpha-50);
      color: var(--content-foundation-content-warning-light);
    }

    &:hover:not(:disabled) {
      background-color: var(--background-surface-core-background-warning-light);
    }

    &:focus:not(:disabled) {
      background-color: var(
        --background-surface-core-background-warning-state-focus
      );
    }

    &.loading {
      border-color: var(--border-foundation-border-warning);
    }

    & .SpinnerItem {
      background-color: var(--color-orange-500);
    }
  }

  &.light {
    background-color: var(--background-surface-core-background-warning-light);
    border-color: var(--background-surface-core-background-warning-light);
    color: var(--content-foundation-content-warning);

    &:disabled {
      color: var(--content-foundation-content-warning-light);
    }

    &:disabled,
    &:focus:not(:disabled) {
      background-color: var(--background-surface-core-background-warning-light);
    }

    &:hover:not(:disabled) {
      background: var(--background-surface-core-background-warning-state-focus);
    }

    &:focus:not(:disabled) {
      border-color: var(--border-core-border-warning-light);
    }

    & .SpinnerItem {
      background-color: var(--color-orange-500);
    }
  }
}

.red {
  background-color: var(--background-surface-foundation-background-danger);
  border-color: var(--background-surface-foundation-background-danger);
  color: var(--white);

  &:disabled {
    background-color: var(--background-surface-core-background-danger-dull);
  }

  &:focus:not(:disabled),
  &:disabled.loading {
    background-color: var(--background-surface-foundation-background-danger);
  }

  &:focus:not(:disabled) {
    border-color: var(--border-core-border-danger-light);
  }

  &:hover:not(:disabled) {
    background-color: var(--background-surface-core-background-danger-dark);
  }

  &.outlined {
    color: var(--background-surface-foundation-background-danger);

    &:disabled {
      background-color: var(--background-surface-foundation-background-primary);
      border-color: var(--color-red-500-alpha-50);
      color: var(--content-core-content-danger-light);
    }

    &:hover:not(:disabled) {
      background-color: var(--background-surface-core-background-danger-light);
    }

    &:focus:not(:disabled) {
      background-color: var(
        --background-surface-core-background-danger-state-focus
      );
    }

    &.loading {
      border-color: var(--border-foundation-border-danger);
    }

    & .SpinnerItem {
      background-color: var(--color-red-500);
    }
  }

  &.light {
    background-color: var(--background-surface-core-background-danger-light);
    border-color: var(--background-surface-core-background-danger-light);
    color: var(--content-foundation-content-danger);

    &:disabled {
      color: var(--content-core-content-danger-light);
    }

    &:disabled,
    &:focus:not(:disabled) {
      background-color: var(--background-surface-core-background-danger-light);
    }

    &:hover:not(:disabled) {
      background: var(--background-surface-core-background-danger-state-focus);
    }

    &:focus:not(:disabled) {
      border-color: var(--border-core-border-danger-light);
    }

    & .SpinnerItem {
      background-color: var(--color-red-500);
    }
  }
}

.green {
  background-color: var(--background-surface-foundation-background-success);
  border-color: var(--background-surface-foundation-background-success);
  color: var(--white);

  &:disabled {
    background-color: var(--background-surface-core-background-success-dull);
  }

  &:focus:not(:disabled),
  &:disabled.loading {
    background-color: var(--background-surface-foundation-background-success);
  }

  &:focus:not(:disabled) {
    border-color: var(--border-core-border-success-light);
  }

  &:hover:not(:disabled) {
    background-color: var(--background-surface-core-background-success-dark);
  }

  &.outlined {
    color: var(--background-surface-foundation-background-success);

    &:hover {
      color: var(--white);
    }

    & .SpinnerItem {
      background-color: var(--color-green-500);
    }
  }

  &.light {
    background-color: var(--background-surface-core-background-success-light);
    border-color: var(--background-surface-core-background-success-light);
    color: var(--content-foundation-content-success);

    &:disabled {
      color: var(--content-foundation-content-success-light);
    }

    &:disabled,
    &:focus:not(:disabled) {
      background-color: var(--background-surface-core-background-success-light);
    }

    &:hover:not(:disabled) {
      background: var(--background-surface-core-background-success-state-focus);
    }

    &:focus:not(:disabled) {
      border-color: var(--border-core-border-success-light);
    }

    & .SpinnerItem {
      background-color: var(--color-green-500);
    }
  }
}

.grey {
  background-color: var(--background-surface-foundation-background-tertiary);
  border-color: var(--background-surface-foundation-background-tertiary);
  color: var(--content-foundation-content-tertiary);

  &:disabled {
    background-color: var(
      --background-surface-core-background-primary-state-disabled
    );
  }

  &:disabled.loading {
    background-color: var(--background-surface-foundation-background-tertiary);
  }

  &:focus:not(:disabled) {
    background-color: var(
      --background-surface-core-background-tertiary-state-hover
    );
    border: 1px solid var(--border-foundation-border-secondary);
  }

  &:hover:not(:disabled) {
    background-color: var(
      --background-surface-core-background-primary-state-hover
    );
  }

  &.light {
    background-color: var(--background-surface-foundation-background-tertiary);
    color: var(--content-foundation-content-tertiary);

    &:disabled {
      background-color: var(
        --background-surface-foundation-background-tertiary
      );
      color: var(--content-core-content-state-disabled);
    }

    &:hover:not(:disabled),
    &:focus:not(:disabled) {
      background: var(
        --background-surface-core-background-tertiary-state-hover
      );
    }

    &:focus:not(:disabled) {
      border-color: var(--border-foundation-border-selected);
    }

    & .SpinnerItem {
      background-color: var(--content-foundation-content-tertiary);
    }
  }

  &.inverse {
    .ButtonChild {
      color: var(--content-core-content-inverse-primary);
    }

    &:disabled {
      background-color: transparent;
      opacity: 80%;

      &:hover {
        background-color: transparent;
      }

      &:focus {
        background-color: transparent;
      }
    }

    &:hover:not(:disabled) {
      background-color: var(--content-core-content-inverse-primary);

      .ButtonChild {
        color: var(--content-core-content-inverse-tertiary);
      }
    }

    &:focus:not(:disabled) {
      background-color: var(--content-core-content-inverse-primary);

      .ButtonChild {
        color: var(--content-core-content-inverse-tertiary);
      }
    }
  }
}

.light {
  & .SpinnerItem:nth-child(2) {
    opacity: 50%;
  }

  & .SpinnerItem:nth-child(3) {
    opacity: 25%;
  }
}

.rounded {
  border-radius: var(--border-radius-rounded-circular);
}

.outlined {
  background-color: var(--background-surface-foundation-background-primary);
  box-shadow: none;
}

.grey.outlined {
  border: 1px solid var(--border-foundation-border-primary);
  color: var(--content-foundation-content-tertiary);

  &:hover {
    background-color: var(--color-gray-200);
  }

  & .SpinnerItem {
    background-color: var(--color-gray-300);
  }
}

/* =================================================================
   Design system variants — PRIMITIVES.md §01
   primary | accent | secondary (alias: outline) | ghost
   | destructive (alias: danger) | dangerSolid
   ================================================================= */

.rv {
  border-radius: var(--rv-radius-md);
  transition:
    background-color var(--rv-dur-fast) var(--rv-ease-out),
    border-color var(--rv-dur-fast) var(--rv-ease-out),
    color var(--rv-dur-fast) var(--rv-ease-out),
    box-shadow var(--rv-dur-fast) var(--rv-ease-out);

  & svg {
    height: var(--rv-icon-sm);
    width: var(--rv-icon-sm);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 40%;
  }

  &:focus-visible:not(:disabled) {
    box-shadow: var(--rv-shadow-ring-focus);
    outline: none;
  }
}

/* primary — default CTA, dark neutral-900 fill */
.primary {
  background-color: var(--rv-fg-1);
  border: 1px solid var(--rv-fg-1);
  color: var(--white);

  &:hover:not(:disabled) {
    background-color: var(--rv-neutral-800);
    border-color: var(--rv-neutral-800);
  }

  &:active:not(:disabled) {
    background-color: var(--rv-neutral-700);
    border-color: var(--rv-neutral-700);
  }
}

/* In dark mode rv-fg-1 flips to near-white — keep text readable by inverting to bg-app */
html[data-color-mode='dark'] .rv.primary {
  color: var(--rv-bg-app);

  &:hover:not(:disabled) {
    background-color: var(--rv-fg-2);
    border-color: var(--rv-fg-2);
  }

  &:active:not(:disabled) {
    background-color: var(--rv-fg-3);
    border-color: var(--rv-fg-3);
  }
}

/* accent — single most important action; max one per view */
.accent {
  background-color: var(--rv-accent);
  border: 1px solid var(--rv-accent);
  color: var(--rv-accent-ink-deep);

  &:hover:not(:disabled) {
    background-color: var(--rv-accent-hover);
    border-color: var(--rv-accent-hover);
    box-shadow: var(--rv-shadow-cta-hover);
  }

  &:active:not(:disabled) {
    background-color: var(--rv-accent-active);
    border-color: var(--rv-accent-active);
  }
}

/* secondary / outline — neutral border, white fill, paired with primary */
.secondary,
.outline {
  background-color: var(--rv-surface);
  border: 1px solid var(--rv-border);
  color: var(--rv-fg-1);

  &:hover:not(:disabled) {
    background-color: var(--rv-bg-app-subtle);
    border-color: var(--rv-neutral-300);
  }

  &:active:not(:disabled) {
    background-color: var(--rv-neutral-100);
  }
}

/* ghost — tertiary / inline, no border at rest */
.ghost {
  background-color: transparent;
  border: 1px solid transparent;
  color: var(--rv-fg-2);

  &:hover:not(:disabled) {
    background-color: var(--rv-bg-app-subtle);
    color: var(--rv-fg-1);
  }

  &:active:not(:disabled) {
    background-color: var(--rv-neutral-100);
  }
}

/* destructive / danger — destructive secondary, red border + white fill */
.destructive,
.danger {
  background-color: var(--rv-surface);
  border: 1px solid var(--rv-danger-bg);
  color: var(--rv-danger-ink);

  &:hover:not(:disabled) {
    background-color: var(--rv-danger-bg);
    border-color: var(--rv-danger);
  }

  &:active:not(:disabled) {
    background-color: var(--rv-danger);
    color: var(--white);
  }

  &:focus-visible:not(:disabled) {
    box-shadow: 0 0 0 3px var(--color-red-500-alpha-25);
  }
}

/* dangerSolid — destructive primary in confirmation modal */
.dangerSolid {
  background-color: var(--rv-danger);
  border: 1px solid var(--rv-danger);
  color: var(--white);

  &:hover:not(:disabled) {
    background-color: var(--color-red-600);
    border-color: var(--color-red-600);
  }

  &:active:not(:disabled) {
    background-color: var(--color-red-700);
    border-color: var(--color-red-700);
  }

  &:focus-visible:not(:disabled) {
    box-shadow: 0 0 0 3px var(--color-red-500-alpha-25);
  }
}

/* ---------- Sizes (xs | sm | md | lg) — apply to design system variants ---------- */
.xs {
  border-radius: var(--rv-radius-sm);
  font-size: var(--rv-text-xs);
  gap: var(--rv-space-1);
  height: var(--rv-space-6);
  padding: 0 var(--rv-space-2);
}

.sm {
  font-size: var(--rv-text-xs);
  gap: var(--rv-space-1);
  height: var(--rv-space-8);
  padding: 0 var(--rv-space-3);
}

.md {
  font-size: var(--rv-text-sm);
  gap: var(--rv-space-2);
  height: var(--rv-space-10);
  padding: 0 var(--rv-space-4);
}

.lg {
  border-radius: var(--rv-radius-lg);
  font-size: var(--rv-text-base);
  gap: var(--rv-space-2);
  height: var(--rv-space-12);
  padding: 0 var(--rv-space-5);
}
